// src/components/MissionForm.jsx
import React, { useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { collection, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { GTA_GAME_TABS as GAMES } from "../components/ToolbarHeader";

const CATEGORIES = ["Main Story", "Side Mission", "Custom Mission"];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const SAMPLE_MISSION = {
  // ===== NUMBERING & RELATION =====
  displayNumber: "8",
  missionNumber: 8,
  subMissionNumber: null,
  parentMissionId: "gta-3-side-missions",
  arcOrder: 8,

  // ===== CORE INFO =====
  missionName: "Big'N'Veiny",
  category: "Side Mission",
  chapter: "Shoreside Vale",
  location: "Cochrane Dam, Shoreside Vale",
  difficulty: "Hard",
  estDurationMins: "6",

  game: "GTA III",
  gameKey: "gta-3",

  // ===== VARIANT META =====
  isParent: false,
  isVariant: false,
  variantType: "checkpoint-truck-challenge",
  countsToward100: true,

  // ===== REWARDS =====
  rewardsCash: 20000,
  rewardsItems: "Completes Big'N'Veiny challenge",

  // ===== TAGS =====
  tagsCsv: "checkpoint,truck,dam,challenge",

  // ===== MEDIA =====
  coverImage: "",
  gallery: [],
  videoUrl: "",

  // ===== WALKTHROUGH =====
  walkthrough: `### Big'N'Veiny Walkthrough
1. Start the mission at Cochrane Dam.
2. Enter the Patriot truck.
3. Drive through all checkpoints before time runs out.
4. Avoid crashing or flipping the heavy vehicle.
5. Complete the final checkpoint to finish the mission.`,

  // ===== TIPS =====
  tips: `- Slow down before sharp turns.
- Use handbrake lightly to control slides.
- Avoid overcorrecting steering.`,

  // ===== APPROACH NOTES =====
  approaches: `- **Controlled Driving:** Focus on stability over speed.
- **Route Memory:** Anticipate turns early due to vehicle weight.`,

  // ===== STORY =====
  story: `Claude completes a dangerous checkpoint run across Cochrane Dam using a heavy military truck, proving mastery over extreme driving challenges.`,

  // ===== OBJECTIVES =====
  primaryObjectives: [
    "Enter the Patriot truck",
    "Pass all checkpoints",
    "Finish before time expires"
  ],

  optionalObjectives: [],

  // ===== EXTRA =====
  arcId: "gta-3-side-missions",
  shortNote: "Final non-repeatable side mission in GTA III.",
  city: "Liberty City",

  mapImage: "",
  xPct: "12.9",
  yPct: "18.7",

  // ===== CLASSIFICATION LOCK =====
  missionTypeSource: "rockstar-canonical",
};

export default function MissionForm({
  user,
  enforceAdmin = false,
  isAdmin = false,
  isSuperAdmin = false,
  onSaved,
  onCancel,
  initial = {},
  missionId = null,
  mode = "create", // "create" | "edit"
}) {
  const isAuthed = Boolean(user?.uid);
  const blockedByAdminGuard = enforceAdmin && (!isAuthed || !isAdmin);
  const canUseAdvancedFields = isAdmin || isSuperAdmin;
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const titleRef = React.useRef(null);
  const firstPrimaryObjectiveRef = React.useRef(null);

  const RequiredLabel = ({ children }) => (
    <label className="mt6-label">
      {children}
      <span className="mt6-required" aria-hidden="true"> *</span>
    </label>
  );

  // put above the component or inside it
  const normalizeInitial = (init = {}) => {
    const obj = init || {};
    const tagsCsv = Array.isArray(obj.tags) ? obj.tags.join(", ") : (obj.tagsCsv || "");

    return {
      // direct
      missionName: obj.missionName ?? "",
      game: obj.game ?? "GTA VI",
      gameKey: obj.gameKey ?? "gta-6",
      category: obj.category ?? "Main Story",
      missionNumber: obj.missionNumber ?? "",
      displayNumber: obj.displayNumber ?? "",
      subMissionNumber: obj.subMissionNumber ?? null,
      parentMissionId: obj.parentMissionId ?? null,
      isVariant: typeof obj.isVariant === "boolean" ? obj.isVariant : false,
      isParent: typeof obj.isParent === "boolean" ? obj.isParent : false,
      variantType: obj.variantType ?? null,
      countsToward69:
        typeof obj.countsToward69 === "boolean" ? obj.countsToward69 : false,
      chapter: obj.chapter ?? "",
      location: obj.location ?? "",
      difficulty: obj.difficulty ?? "Medium",
      estDurationMins: obj.estDurationMins ?? "",
      rewardsCash: obj.rewards?.cash ?? obj.rewardsCash ?? "",
      rewardsItems: obj.rewards?.items ?? obj.rewardsItems ?? "",
      tagsCsv,
      coverImage: obj.coverImage ?? "",
      gallery: Array.isArray(obj.gallery) && obj.gallery.length ? obj.gallery : [""],
      videoUrl: obj.videoUrl ?? "",
      walkthrough: obj.walkthrough ?? "",
      tips: obj.tips ?? "",
      approaches: obj.approaches ?? "",
      story: obj.story ?? "",
      primaryObjectives:
        (obj.objectives?.primary?.length ? obj.objectives.primary : obj.primaryObjectives) ?? [""],
      optionalObjectives:
        (obj.objectives?.optional?.length ? obj.objectives.optional : obj.optionalObjectives) ?? [""],
      arcId: obj.arcId ?? "",
      shortNote: obj.shortNote ?? "",
      city: obj.city ?? "",
      // visibility
      isPrivate: typeof obj.isPrivate === "boolean" ? obj.isPrivate : true,
    };
  };

  const [form, setForm] = useState({
    missionName: "",
    game: "GTA VI",
    gameKey: "gta-6",
    category: "Main Story",
    missionNumber: "",
    displayNumber: "",
    subMissionNumber: null,
    parentMissionId: null,
    isVariant: false,
    isParent: false,
    variantType: null,
    countsToward69: false,
    chapter: "",
    location: "",
    difficulty: "Medium",
    estDurationMins: "",
    rewardsCash: "",
    rewardsItems: "",
    tagsCsv: "",
    coverImage: "",
    gallery: [""],
    videoUrl: "",
    walkthrough: "",
    tips: "",
    approaches: "",
    story: "",
    primaryObjectives: [""],
    optionalObjectives: [""],
    arcId: "",
    shortNote: "",
    city: "",
    isPrivate: true,
    ...normalizeInitial(initial),
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState({
    walkthrough: false,
    tips: false,
    approaches: false,
    story: false,
  });

  const updateField = (name, value) =>
    setForm((s) => ({ ...s, [name]: value }));

  const updateArrayField = (arrKey, idx, value) =>
    setForm((s) => {
      const arr = [...s[arrKey]];
      arr[idx] = value;
      return { ...s, [arrKey]: arr };
    });

  const addArrayRow = (arrKey, blank = "") =>
    setForm((s) => ({ ...s, [arrKey]: [...s[arrKey], blank] }));

  const removeArrayRow = (arrKey, idx) =>
    setForm((s) => {
      const arr = s[arrKey].filter((_, i) => i !== idx);
      return { ...s, [arrKey]: arr.length ? arr : [""] };
    });

  const togglePreview = (key) => setPreviews((p) => ({ ...p, [key]: !p[key] }));

  // ---------- validation ----------
  const issues = useMemo(() => {
    const errs = [];

    if (!form.missionName.trim()) {
      errs.push("Title is required.");
    }

    const primaryFilled = form.primaryObjectives.filter(t => t.trim()).length;
    if (primaryFilled < 1) {
      errs.push("At least one Primary Objective is required.");
    }

    return errs;
  }, [form]);

  const missingRequiredFields = useMemo(() => {
    const missing = [];

    if (!form.missionName.trim()) {
      missing.push("Title");
    }

    const primaryFilled = form.primaryObjectives.filter(t => t.trim()).length;
    if (primaryFilled < 1) {
      missing.push("Primary Objectives");
    }

    return missing;
  }, [form]);

  const hasTitleError =
    attemptedSubmit && !form.missionName.trim();

  const hasPrimaryObjectivesError =
    attemptedSubmit &&
    form.primaryObjectives.filter(t => t.trim()).length < 1;

  const canSubmit = issues.length === 0 && !saving;

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();

    // Compute validation NOW (sync)
    const titleInvalid = !form.missionName.trim();
    const primaryInvalid =
      form.primaryObjectives.filter(t => t.trim()).length < 1;

    setAttemptedSubmit(true);

    if (titleInvalid || primaryInvalid) {
      // Scroll immediately on FIRST click
      if (titleInvalid && titleRef.current) {
        titleRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        titleRef.current.focus();
      } else if (primaryInvalid && firstPrimaryObjectiveRef.current) {
        firstPrimaryObjectiveRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        firstPrimaryObjectiveRef.current.focus();
      }
      return;
    }

    // ---- VALID ----
    setSaving(true);
    setError("");


    setSaving(true);
    setError("");

    try {
      // Normalize game fields from tabs in case labels changed
      const chosen = GAMES.find((g) => g.key === form.gameKey);
      const normalizedGame = chosen ? chosen.label : form.game;
      const existingCreatedBy = initial?.createdBy ?? user.uid;
      const isOfficial = isSuperAdmin;
      const autoSideTags =
        form.category === "Side Mission" && form.arcId
          ? [form.arcId.split("-")[0]]
          : [];

      const tags = [
        ...new Set([
          ...autoSideTags,
          ...form.tagsCsv
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        ]),
      ];

      const payload = {
        game: normalizedGame,
        gameKey: form.gameKey,
        missionName: form.missionName.trim(),
        displayNumber: form.displayNumber || null,
        subMissionNumber:
          form.subMissionNumber === null || form.subMissionNumber === ""
            ? null
            : Number(form.subMissionNumber),
        parentMissionId: form.parentMissionId || null,
        isVariant: Boolean(form.isVariant),
        isParent: Boolean(form.isParent),
        variantType: form.variantType || null,
        countsToward69: Boolean(form.countsToward69),
        category: form.category,
        missionNumber:
          form.missionNumber === "" ? null : Number(form.missionNumber),
        chapter: form.chapter.trim() || null,
        location: form.location.trim() || null,
        difficulty: form.difficulty || null,
        estDurationMins:
          form.estDurationMins === "" ? null : Number(form.estDurationMins),
        rewards: {
          cash: form.rewardsCash === "" ? null : Number(form.rewardsCash),
          items: form.rewardsItems.trim() || null,
        },
        tags: tags,

        coverImage: form.coverImage.trim() || null,
        gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
        videoUrl: form.videoUrl.trim() || null,
        walkthrough: form.walkthrough.trim(),
        tips: form.tips.trim(),
        approaches: form.approaches.trim(),
        story: form.story.trim(),
        objectives: {
          primary: form.primaryObjectives.map((t) => t.trim()).filter(Boolean),
          optional: form.optionalObjectives
            .map((t) => t.trim())
            .filter(Boolean),
        },
        arcId: form.arcId.trim(),
        shortNote: form.shortNote.trim() || null,
        city: form.city.trim() || null,
        createdBy: user.uid,
        createdByRole: isOfficial ? "admin" : "fan",
        fanCreated: !isOfficial, // legacy + compatibility
        source: isOfficial ? "official" : "community",
        status: isOfficial ? "approved" : "pending",
        isPrivate: Boolean(form.isPrivate),
        updatedAt: serverTimestamp(),
      };

      if (missionId) {
        // Editing existing mission

        payload.createdBy = existingCreatedBy;
        await updateDoc(doc(db, "missions", missionId), payload);
        onSaved?.(missionId);
      } else {
        // Creating new mission
        payload.createdBy = user.uid;
        payload.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, "missions"), payload);
        onSaved?.(ref.id);
      }
    } catch (err) {
      console.error(err);
      setError("Could not save mission. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const loadSampleMission = () => {
    setForm((s) => ({
      ...s,
      ...SAMPLE_MISSION,
      isPrivate: s.isPrivate, // keep current selection
    }));
  };

  // When user changes game via select, set both label and key
  const setGameByKey = (key) => {
    const item = GAMES.find((g) => g.key === key);
    if (!item) return;
    setForm((s) => ({ ...s, game: item.label, gameKey: item.key }));
  };

  useEffect(() => {
    // when editing and a mission arrives from Firestore, merge it in
    if (initial && Object.keys(initial).length) {
      setForm((prev) => ({ ...prev, ...normalizeInitial(initial) }));
    }
  }, [initial]);

  return (
    <>
      {error && <div className="mt6-banner mt6-banner--error">{error}</div>}

      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- Basics ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Basics</h4>
          {/* Game */}
          {/* Game (UI only shows the friendly select; gameKey stays in state) */}
          <div>
            <label className="mt6-label">Game</label>
            <select
              className="mt6-input"
              value={form.gameKey}
              onChange={(e) => setGameByKey(e.target.value)}
              disabled={blockedByAdminGuard || saving}
              aria-label="Select game"
              title="Select game"
            >
              {GAMES.filter((g) => g.key !== "all").map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <RequiredLabel>Title</RequiredLabel>
          <input
            ref={titleRef}
            className={`mt6-input ${hasTitleError ? "mt6-input--error" : ""}`}
            value={form.missionName}
            onChange={(e) => updateField("missionName", e.target.value)}
            placeholder="e.g., Undercover Assignment"
            disabled={blockedByAdminGuard || saving}
          />

          {/* Row 1: Mission # + Chapter */}
          <div className="mt6-row-2">
            <div>
              <label className="mt6-label">Mission # (optional)</label>
              <input
                className="mt6-input"
                value={form.missionNumber}
                onChange={(e) => updateField("missionNumber", e.target.value)}
                placeholder="e.g., 12"
                disabled={blockedByAdminGuard || saving}
              />
            </div>

            <div>
              <label className="mt6-label">Chapter / Act (optional)</label>
              <input
                className="mt6-input"
                value={form.chapter}
                onChange={(e) => updateField("chapter", e.target.value)}
                placeholder="e.g., Act II"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          {/* Row 2: Category + Visibility */}
          <div className="mt6-row-2">
            {/* Category */}
            <div>
              <label className="mt6-label">Category</label>
              <div className="mt6-pills">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`mt6-pill ${form.category === c ? "is-active" : ""
                      }`}
                    onClick={() => updateField("category", c)}
                    disabled={blockedByAdminGuard || saving}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="mt6-label">Visibility</label>
              <div className="mt6-pills mt6-vis-inline">
                {[
                  { label: "Public", value: false },
                  { label: "Private", value: true },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    className={`mt6-pill ${form.isPrivate === opt.value ? "is-active" : ""
                      }`}
                    onClick={() => updateField("isPrivate", opt.value)}
                    aria-pressed={form.isPrivate === opt.value}
                    disabled={blockedByAdminGuard || saving}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="mt6-muted mt6-vis-status" aria-live="polite">
                {form.isPrivate
                  ? "Private: visible only to you."
                  : "Public: visible to everyone."}
              </div>
            </div>
          </div>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Location (optional)</label>
              <input
                className="mt6-input"
                value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
                placeholder="e.g., Vice City – Port Area"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Difficulty (optional)</label>
              <select
                className="mt6-input"
                value={form.difficulty}
                onChange={(e) => updateField("difficulty", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              >
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mt6-label">Est. Duration (mins)</label>
              <input
                className="mt6-input"
                value={form.estDurationMins}
                onChange={(e) => updateField("estDurationMins", e.target.value)}
                placeholder="e.g., 15"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>
        </div>

        {/* ---- Objectives ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Objectives</h4>
          <RequiredLabel>Primary Objectives</RequiredLabel>
          {form.primaryObjectives.map((t, i) => (
            <div className="mt6-row" key={`po-${i}`}>
              <input
                ref={i === 0 ? firstPrimaryObjectiveRef : null}
                className={`mt6-input ${hasPrimaryObjectivesError && !t.trim()
                  ? "mt6-input--error"
                  : ""
                  }`}
                value={t}
                onChange={(e) =>
                  updateArrayField("primaryObjectives", i, e.target.value)
                }
                placeholder={`Objective ${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("primaryObjectives", i)}
                disabled={
                  blockedByAdminGuard ||
                  saving ||
                  form.primaryObjectives.length <= 1
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={() => addArrayRow("primaryObjectives")}
            disabled={blockedByAdminGuard || saving}
          >
            ＋ Add Objective
          </button>

          <label className="mt6-label">Optional Objectives</label>
          {form.optionalObjectives.map((t, i) => (
            <div className="mt6-row" key={`oo-${i}`}>
              <input
                className="mt6-input"
                value={t}
                onChange={(e) =>
                  updateArrayField("optionalObjectives", i, e.target.value)
                }
                placeholder={`Optional Objective ${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("optionalObjectives", i)}
                disabled={
                  blockedByAdminGuard ||
                  saving ||
                  form.optionalObjectives.length <= 1
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={() => addArrayRow("optionalObjectives")}
            disabled={blockedByAdminGuard || saving}
          >
            ＋ Add Optional
          </button>
        </div>

        {/* ---- Content ---- */}
        {["walkthrough", "tips", "approaches", "story"].map((key) => (
          <div className="mt6-section" key={key}>
            <h4 className="mt6-h4">
              {
                {
                  walkthrough: "Walkthrough",
                  tips: "Tips & Tricks",
                  approaches: "Different Approaches",
                  story: "Story Summary",
                }[key]
              }
            </h4>
            <div className="mt6-segment">
              <button
                type="button"
                className={`mt6-seg-btn ${!previews[key] ? "is-active" : ""}`}
                onClick={() => togglePreview(key)}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previews[key] ? "is-active" : ""}`}
                onClick={() => togglePreview(key)}
              >
                Preview
              </button>
            </div>
            {!previews[key] ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                value={form[key]}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={
                  {
                    walkthrough: "Step-by-step guide…",
                    tips: "Helpful hints, loadouts, vehicles…",
                    approaches: "Stealth, loud, alternate routes…",
                    story: "Spoiler-safe synopsis…",
                  }[key]
                }
                disabled={blockedByAdminGuard || saving}
              />
            ) : (
              <div className="mt6-preview">
                {form[key].trim() ? (
                  <ReactMarkdown>{form[key]}</ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* ---- Rewards / Media ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Rewards</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Cash (optional)</label>
              <input
                className="mt6-input"
                value={form.rewardsCash}
                onChange={(e) => updateField("rewardsCash", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              />
            </div>

            <div>
              <label className="mt6-label">Items (optional)</label>
              <input
                className="mt6-input"
                value={form.rewardsItems}
                onChange={(e) => updateField("rewardsItems", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              />
            </div>

            <div>
              <label className="mt6-label">Tags (CSV)</label>
              <input
                className="mt6-input"
                value={form.tagsCsv}
                onChange={(e) => updateField("tagsCsv", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          {/* MEDIA — admins only */}
          {canUseAdvancedFields && (
            <>
              <h5 className="mt6-h5 mt6-mt">Media (Admin Only)</h5>

              <label className="mt6-label">Cover Image URL</label>
              <input
                className="mt6-input"
                value={form.coverImage}
                onChange={(e) => updateField("coverImage", e.target.value)}
                disabled={saving}
              />

              <label className="mt6-label">Gallery URLs</label>
              {form.gallery.map((u, i) => (
                <div className="mt6-row" key={`g-${i}`}>
                  <input
                    className="mt6-input"
                    value={u}
                    onChange={(e) => updateArrayField("gallery", i, e.target.value)}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="mt6-remove"
                    onClick={() => removeArrayRow("gallery", i)}
                    disabled={saving || form.gallery.length <= 1}
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="mt6-add"
                onClick={() => addArrayRow("gallery")}
                disabled={saving}
              >
                ＋ Add Image
              </button>

              <label className="mt6-label">Video URL</label>
              <input
                className="mt6-input"
                value={form.videoUrl}
                onChange={(e) => updateField("videoUrl", e.target.value)}
                disabled={saving}
              />
            </>
          )}
        </div>

        {/* ---- Actions ---- */}
        <div className="mt6-actions">
          <button
            type="submit"
            className="mt6-btn mt6-btn--primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Mission"}
          </button>

          {onCancel && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </button>
          )}

          {isSuperAdmin && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              onClick={loadSampleMission}
              disabled={saving}
              title="Fill form with sample mission data"
            >
              ⚡ Load Sample Mission
            </button>
          )}

        </div>
      </form>
    </>
  );
}
